import React from 'react';
import { Box, Typography } from '@mui/material';
import styles from './sharedStyles';
import {
  SQUAD_PREVIEW_TITLE_WIDTH_PX,
  useSquadTitleFontSize,
} from '../squadTitleFit';
import { filterSquadKvRows, splitSquadKvDash, squadKeepNumberWithUnit } from '../squadKvRows';
import { parseSquadInlineText } from '../squadInlineText';
import { SQUAD_COLORS } from '../templateConstants';
import { splitSquadTitle, SQUAD_PAGE_HEADER, SQUAD_SECTION_GEOMETRY_PREVIEW, SQUAD_TYPOGRAPHY } from '../pageHeaderTokens';
import { SquadFondsLogoPreview } from '../SquadFondsLogo';
import { kalenderjahrHasData, normalizeKalenderjahrData } from '../kalenderjahr';

export const SquadLogo: React.FC = () => (
  <Box sx={styles.logoBlock}>
    <SquadFondsLogoPreview
      width={SQUAD_PAGE_HEADER.logoImageWidth}
      height={SQUAD_PAGE_HEADER.logoImageHeight}
    />
  </Box>
);

export const FundTitle: React.FC<{ title: string; primary?: string; secondary?: string }> = ({
  title,
  primary = SQUAD_COLORS.navy,
  secondary = SQUAD_COLORS.gold,
}) => {
  const { squad, rest } = splitSquadTitle(title);
  /** Same autofit as the PDF, just against the preview title column width (px). */
  const fontSize = useSquadTitleFontSize(title, SQUAD_PREVIEW_TITLE_WIDTH_PX);

  if (squad) {
    return (
      <Box sx={{ ...styles.mainTitle, fontSize }}>
        <Box component="span" sx={{ ...styles.titleSquad, color: secondary }}>
          {squad}
        </Box>
        {rest ? (
          <Box component="span" sx={{ ...styles.titleRest, color: primary }}>
            {' '}
            {rest}
          </Box>
        ) : null}
      </Box>
    );
  }
  return (
    <Box sx={{ ...styles.mainTitle, fontSize, color: primary }}>{title}</Box>
  );
};

export interface SquadPageHeaderProps {
  page: number;
  total?: number;
  title: string;
  primary?: string;
  secondary?: string;
  /** Left meta label, e.g. „Werbemitteilung (Stand: 30.09.2026)“ — see squadPageMetaLabel. */
  metaLabel?: string;
}

export const SquadPageHeader: React.FC<SquadPageHeaderProps> = ({
  page,
  total = 4,
  title,
  primary,
  secondary,
  metaLabel = 'Werbemitteilung',
}) => (
  <Box sx={styles.pageHeader}>
    <Box sx={styles.headerSidebarCell}>
      <SquadLogo />
    </Box>
    <Box sx={styles.headerMainCell}>
      <FundTitle title={title} primary={primary} secondary={secondary} />
      <Box sx={styles.headerMetaRow}>
        <Box sx={{ ...styles.headerMetaText, ...styles.headerMetaTextLeft }}>{metaLabel}</Box>
        <Box sx={{ ...styles.headerMetaText, ...styles.headerMetaTextRight }}>
          Seite {page} von {total}
        </Box>
      </Box>
    </Box>
  </Box>
);

export const NavyBar: React.FC<{ children: React.ReactNode; color?: string }> = ({
  children,
  color = SQUAD_COLORS.navy,
}) => (
  <Box sx={{ ...styles.navyBar, backgroundColor: color }}>{children}</Box>
);

export const GoldBar: React.FC<{ children: React.ReactNode; textColor?: string }> = ({
  children,
  textColor = SQUAD_COLORS.navy,
}) => (
  <Box
    sx={{
      ...styles.goldBar,
      color: textColor,
      borderTopColor: textColor,
      borderBottomColor: textColor,
    }}
  >
    {children}
  </Box>
);

export const SidebarSectionTitle: React.FC<{ children: React.ReactNode; color?: string }> = ({
  children,
  color = SQUAD_COLORS.navy,
}) => (
  <Box sx={{ ...styles.sidebarSectionTitle, color }}>{children}</Box>
);

export const KeyValueTable: React.FC<{
  rows: Array<{ label: string; value: string }>;
  color?: string;
  /** Breite der Label-Spalte in % (Standard 60 wie im Original; Gebühren 55). */
  labelWidthPct?: number;
}> = ({ rows, color = SQUAD_COLORS.navy, labelWidthPct }) => {
  const labelSx = labelWidthPct ? { flex: `0 0 ${labelWidthPct}%`, maxWidth: `${labelWidthPct}%` } : null;
  const valueSx = labelWidthPct ? { flex: `0 0 ${100 - labelWidthPct}%`, maxWidth: `${100 - labelWidthPct}%` } : null;
  const visible = filterSquadKvRows(rows || []);
  if (!visible.length) return null;
  return (
  <Box>
    {visible.map((row, i) => (
      <Box
        key={`${row.label}-${i}`}
        sx={{
          ...styles.kvRow,
          borderBottomColor: color,
          ...(i === visible.length - 1 ? { borderBottom: 'none' } : null),
        }}
      >
        <Box sx={{ ...styles.kvLabel, ...labelSx, color }}>
          {(() => {
            // hängender Einzug bei führendem Spiegelstrich — wie im PDF (splitSquadKvDash)
            const dash = splitSquadKvDash(row.label);
            return dash ? (
              <Box sx={{ display: 'flex' }}>
                <Box component="span" sx={{ flex: `0 0 ${dash.columnEm}em` }}>
                  {dash.dash}
                </Box>
                <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                  {dash.rest}
                </Box>
              </Box>
            ) : (
              row.label
            );
          })()}
        </Box>
        <Box sx={{ ...styles.kvValue, ...valueSx, color }}>
          {/* Line breaks and bold the customer placed — mirrors the PDF */}
          {parseSquadInlineText(squadKeepNumberWithUnit(row.value)).map((line, lineIndex) => (
            <Box key={lineIndex} component="span" sx={{ display: 'block' }}>
              {/* An empty line still occupies one, as it does in the PDF */}
              {line.length === 1 && line[0].text === '' ? '\u00a0' : null}
              {line.map((segment, segmentIndex) => (
                <Box
                  key={segmentIndex}
                  component="span"
                  sx={segment.bold ? { fontWeight: 700 } : undefined}
                >
                  {segment.text}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    ))}
  </Box>
  );
};

export const ImagePlaceholder: React.FC<{
  label: string;
  height?: number | string;
  src?: string;
  /** Keep natural aspect ratio; `height` becomes maxHeight when src is set. */
  preserveAspectRatio?: boolean;
}> = ({ label, height = 80, src, preserveAspectRatio = false }) => {
  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt={label}
        sx={{
          width: '100%',
          height: preserveAspectRatio ? 'auto' : height,
          maxHeight: preserveAspectRatio ? height : undefined,
          objectFit: 'contain',
          display: 'block',
        }}
      />
    );
  }
  return (
    <Box sx={{ ...styles.imagePlaceholder, height }}>
      <Typography sx={{ fontSize: SQUAD_TYPOGRAPHY.placeholder, fontWeight: 600, color: SQUAD_COLORS.goldDark }}>{label}</Typography>
    </Box>
  );
};

/**
 * Page 3 right below Rollierend — mirrors SquadPage3ImagePdf: the box takes the height
 * left in the column, the image fills its width and shrinks (aspect kept, top left) when
 * it is taller. The preview page is not A4-exact, so here the fit is only indicative;
 * the PDF preview measures the real space and says when the image is too tall.
 *
 * Empty, it shows a placeholder — only here, the PDF prints nothing.
 */
export const SquadPage3ImagePreview: React.FC<{ src?: string; label: string }> = ({ src, label }) => (
  <Box
    id="squad-page3-image-section"
    sx={{
      flex: '1 1 0',
      minHeight: src ? 0 : 120,
      position: 'relative',
      mt: `${SQUAD_SECTION_GEOMETRY_PREVIEW.navyBarMarginTop}px`,
    }}
  >
    {src ? (
      <Box
        component="img"
        src={src}
        alt={label}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          objectPosition: 'left top',
          display: 'block',
        }}
      />
    ) : (
      <Box sx={{ ...styles.imagePlaceholder, position: 'absolute', inset: 0, boxSizing: 'border-box', mb: 0 }}>
        <Typography sx={{ fontSize: SQUAD_TYPOGRAPHY.placeholder, fontWeight: 600, color: SQUAD_COLORS.goldDark }}>
          {label}
        </Typography>
      </Box>
    )}
  </Box>
);

/** Wertentwicklung je Kalenderjahr — Jahr/Rendite blocks from TableData (Configure Table). */
export const KalenderjahrPreviewTable: React.FC<{ data?: unknown }> = ({ data }) => {
  const table = normalizeKalenderjahrData(data);
  if (!kalenderjahrHasData(table)) {
    return <ImagePlaceholder label="Kalenderjahr-Performance (Configure Table)" height={56} />;
  }
  const border = '0.5px solid #D0D0D0';
  return (
    <Box
      component="table"
      sx={{
        width: '100%',
        fontSize: SQUAD_TYPOGRAPHY.table,
        borderCollapse: 'collapse',
        color: SQUAD_COLORS.navy,
        tableLayout: 'fixed',
      }}
    >
      <tbody>
        {table.values.map((row, ri) => {
          const isJahr = String(row[0] || '').toLowerCase().includes('jahr');
          return (
            <Box
              component="tr"
              key={ri}
              sx={{ borderTop: ri === 0 ? border : undefined, borderBottom: border }}
            >
              {row.map((cell, ci) => (
                <Box
                  component="td"
                  key={ci}
                  sx={{
                    padding: '5px 4px',
                    textAlign: ci === 0 ? 'left' : 'center',
                    fontWeight: ci === 0 || isJahr ? 700 : 400,
                    width: ci === 0 ? '14%' : undefined,
                    verticalAlign: 'middle',
                  }}
                >
                  {cell || ''}
                </Box>
              ))}
            </Box>
          );
        })}
      </tbody>
    </Box>
  );
};

export const HBarList: React.FC<{
  items: Array<{ label: string; value: number }>;
  color?: string;
  max?: number;
}> = ({ items, color = SQUAD_COLORS.gold, max }) => {
  const peak = max || Math.max(...items.map((i) => Number(i.value) || 0), 1);
  return (
    <Box>
      {items.map((item, idx) => {
        const pct = Math.max(0, Math.min(100, ((Number(item.value) || 0) / peak) * 100));
        return (
          <Box key={idx} sx={styles.barRow}>
            <Box sx={styles.barLabel}>{item.label}</Box>
            <Box sx={styles.barTrack}>
              <Box sx={{ ...styles.barFill, width: `${pct}%`, backgroundColor: color }} />
            </Box>
            <Box sx={styles.barPct}>
              {Number(item.value).toFixed(2).replace('.', ',')}%
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
